import { AgentRun, Client } from '@lucidtech/las-sdk-browser';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { AgentRunId, getAgentRunOptions, toAgentRunId, useFilteredAgentRunsQuery } from './api';
import { useClient } from './useClient';

const findNextRun = async (queryClient: QueryClient, client: Client, runs: AgentRun[]) => {
  if (runs?.length === 0) return;

  for (const candidate of runs) {
    const agentRun = await queryClient.ensureQueryData(
      getAgentRunOptions(client, queryClient, toAgentRunId(candidate.agentId, candidate.runId))
    );

    if (agentRun.status === 'ready-for-review') {
      return agentRun;
    }
  }
};

export const useNextRunId = (agentId: string, runId: string) => {
  const { data } = useFilteredAgentRunsQuery({ agentId });

  const runs = useMemo(() => data?.pages?.flatMap((page) => page.data) ?? [], [data]);
  const currentIndex = useMemo(() => runs.findIndex((run) => run?.runId === runId), [runId, runs]);

  const next = useMemo(() => {
    return runs[currentIndex + 1];
  }, [currentIndex, runs]);

  const previous = useMemo(() => {
    return runs[currentIndex - 1];
  }, [currentIndex, runs]);

  const queryClient = useQueryClient();
  const { client } = useClient();

  const nextToReview = useCallback(
    async (exclusionList?: AgentRunId[]) => {
      const filteredRuns = runs.filter((r) => !exclusionList?.map((e) => e.runId).includes(r.runId));

      const next =
        (await findNextRun(queryClient, client, filteredRuns.slice(currentIndex))) ??
        (await findNextRun(queryClient, client, filteredRuns.slice(0, currentIndex + 1)));

      return next;
    },
    [client, currentIndex, queryClient, runs]
  );

  return {
    currentRunIndex: currentIndex,
    currentRun: runs[currentIndex],
    previousRun: previous,
    nextRun: next,
    nextRunToReview: nextToReview,
    totalRuns: runs.length,
  };
};
